import React from 'react';
import { Crown, Flame, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { USERS, getRankMovement, formatNumber } from '@/lib/mockData';

const top3 = USERS?.slice(0, 3);

const podiumOrder = [top3?.[1], top3?.[0], top3?.[2]]; // Silver, Gold, Bronze display order

const podiumConfig = [
  {
    position: 2,
    label: 'Silver',
    height: 'h-28',
    ringClass: 'avatar-ring-silver',
    borderColor: 'rgba(148, 163, 184, 0.3)',
    bgClass: 'gradient-podium-silver',
    crownColor: '#94a3b8',
    numberColor: '#94a3b8',
    badgeText: '#2',
    badgeBg: 'rgba(148,163,184,0.15)',
    avatarSize: 'w-16 h-16',
    textSize: 'text-xl',
  },
  {
    position: 1,
    label: 'Gold',
    height: 'h-36',
    ringClass: 'avatar-ring-gold',
    borderColor: 'rgba(245, 158, 11, 0.35)',
    bgClass: 'gradient-podium-gold',
    crownColor: '#f59e0b',
    numberColor: '#f59e0b',
    badgeText: '#1',
    badgeBg: 'rgba(245,158,11,0.2)',
    avatarSize: 'w-20 h-20',
    textSize: 'text-2xl',
  },
  {
    position: 3,
    label: 'Bronze',
    height: 'h-20',
    ringClass: 'avatar-ring-bronze',
    borderColor: 'rgba(205, 124, 58, 0.3)',
    bgClass: 'gradient-podium-bronze',
    crownColor: '#cd7c3a',
    numberColor: '#cd7c3a',
    badgeText: '#3',
    badgeBg: 'rgba(205,124,58,0.15)',
    avatarSize: 'w-14 h-14',
    textSize: 'text-lg',
  },
];

export default function PodiumSection() {
  if (!USERS || USERS?.length === 0) {
    return (
      <div className="mb-10 flex flex-col items-center justify-center py-16 gap-4">
        <Crown size={40} style={{ color: '#f59e0b', opacity: 0.3 }} />
        <p className="text-lg font-bold" style={{ color: 'var(--muted-foreground)' }}>
          No one on the podium yet
        </p>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
          Add people to the leaderboard to see the top 3 here.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-end justify-center gap-4 sm:gap-8 pt-8 pb-4">
        {podiumOrder?.map((user, displayIdx) => {
          if (!user) return null;
          const config = podiumConfig?.[displayIdx];
          const movement = getRankMovement(user);
          const isGold = config?.position === 1;

          return (
            <div
              key={`podium-${user?.id}`}
              className="flex flex-col items-center group cursor-pointer"
              style={{ width: isGold ? '200px' : '160px' }}
            >
              {/* Crown for #1 */}
              {isGold && (
                <div className="mb-2 animate-fade-in-up">
                  <Crown size={28} style={{ color: '#f59e0b' }} />
                </div>
              )}
              {/* Avatar */}
              <div className="relative mb-3">
                <div
                  className={`${config?.avatarSize} rounded-full flex items-center justify-center font-bold transition-transform duration-300 group-hover:scale-110`}
                  style={{
                    background: user?.avatarColor,
                    color: '#fff',
                    fontSize: isGold ? '1.25rem' : '1rem',
                    boxShadow: isGold
                      ? `0 0 0 3px #f59e0b, 0 0 30px rgba(245,158,11,0.4)`
                      : config?.position === 2
                      ? `0 0 0 2px #94a3b8, 0 0 20px rgba(148,163,184,0.3)`
                      : `0 0 0 2px #cd7c3a, 0 0 15px rgba(205,124,58,0.25)`,
                  }}
                >
                  {user?.initials}
                </div>

                {/* Rank badge */}
                <div
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold"
                  style={{
                    background: config?.bgClass?.includes('gold') ? '#f59e0b' : config?.position === 2 ? '#94a3b8' : '#cd7c3a',
                    color: '#0a0a0f',
                  }}
                >
                  {config?.position}
                </div>
              </div>
              {/* User info */}
              <div className="text-center mb-3">
                <p
                  className={`font-extrabold leading-tight ${config?.textSize}`}
                  style={{ color: 'var(--foreground)' }}
                >
                  {user?.displayName?.split(' ')?.[0]}
                </p>
                <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                  {user?.username}
                </p>
                <p
                  className="text-sm font-bold mt-1 text-tabular"
                  style={{ color: config?.crownColor }}
                >
                  {formatNumber(user?.score)}
                </p>
              </div>
              {/* Stats mini */}
              <div
                className="w-full flex flex-col gap-1.5 px-4 py-3 rounded-xl"
                style={{
                  background: config?.position === 1
                    ? 'linear-gradient(180deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.04) 100%)'
                    : config?.position === 2
                    ? 'linear-gradient(180deg, rgba(148,163,184,0.1) 0%, rgba(148,163,184,0.03) 100%)'
                    : 'linear-gradient(180deg, rgba(205,124,58,0.1) 0%, rgba(205,124,58,0.03) 100%)',
                  border: `1px solid ${config?.borderColor}`,
                }}
              >
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>Messages</span>
                  <span className="font-bold text-tabular" style={{ color: 'var(--foreground)' }}>
                    {formatNumber(user?.messages)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>Streak</span>
                  <span className="font-bold flex items-center gap-1" style={{ color: '#f97316' }}>
                    <Flame size={11} />
                    {user?.streak}d
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: 'var(--muted-foreground)' }}>Movement</span>
                  <span
                    className="font-bold flex items-center gap-0.5"
                    style={{
                      color: movement?.direction === 'up' ? 'var(--primary)' : movement?.direction === 'down' ? '#ef4444' : 'var(--muted-foreground)',
                    }}
                  >
                    {movement?.direction === 'up' ? <ArrowUp size={10} /> : movement?.direction === 'down' ? <ArrowDown size={10} /> : <Minus size={10} />}
                    {movement?.label}
                  </span>
                </div>
              </div>
              {/* Podium base */}
              <div
                className={`w-full mt-3 rounded-t-lg flex items-center justify-center ${config?.height}`}
                style={{
                  background: config?.position === 1
                    ? 'linear-gradient(180deg, rgba(245,158,11,0.25) 0%, rgba(245,158,11,0.08) 100%)'
                    : config?.position === 2
                    ? 'linear-gradient(180deg, rgba(148,163,184,0.18) 0%, rgba(148,163,184,0.06) 100%)'
                    : 'linear-gradient(180deg, rgba(205,124,58,0.18) 0%, rgba(205,124,58,0.06) 100%)',
                  border: `1px solid ${config?.borderColor}`,
                  borderBottom: 'none',
                }}
              >
                <span
                  className="text-4xl font-extrabold"
                  style={{ color: config?.crownColor, opacity: 0.4 }}
                >
                  {config?.position}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}